import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Users, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ProtectedContent from '../components/ProtectedContent';
import dashboardApi, { DashboardData, handleApiError } from '../services/dashboardService';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import RemindersWidget from '../components/dashboard/RemindersWidget';
import SalesRepDashboard from '../components/dashboard/salesrep/SalesRepDashboard';
import CaseManagerDashboard from '../components/dashboard/casemanager/CaseManagerDashboard';
import AdminFinancialOverview from '../components/dashboard/admin/AdminFinancialOverview';
import AdminSalesMetrics from '../components/dashboard/admin/AdminSalesMetrics';
import AdminCasesMetrics from '../components/dashboard/admin/AdminCasesMetrics';
import AdminSpecialPaymentsWidget from '../components/dashboard/admin/AdminSpecialPaymentsWidget';
import AdminCommissionsOverview from '../components/dashboard/admin/AdminCommissionsOverview';
import CashFlowTrendChart from '../components/dashboard/admin/CashFlowTrendChart';
import AdminAlertsWidget from '../components/dashboard/admin/AdminAlertsWidget';
import StatusDistributionCard from '../components/dashboard/StatusDistributionCard';
import CalendarWidget from '../components/dashboard/CalendarWidget';
import DashboardNavigation, { getAdminDashboardSections } from '../components/dashboard/DashboardNavigation';
import BackToTopButton from '../components/dashboard/BackToTopButton';

const DashboardContainer = styled.div`
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: var(--text-secondary);
`;

const ErrorContainer = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 16px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--error-color);
  margin-bottom: 24px;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
`;

const StatusDistributionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.section`
  scroll-margin-top: 100px;
`;

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardApi.getDashboardData();
      setDashboardData(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleReminderUpdate = () => {
    // Reload dashboard data when reminders are updated
    loadDashboardData();
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>
          טוען נתוני דשבורד...
        </LoadingContainer>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <ErrorContainer>
          {error}
        </ErrorContainer>
      </DashboardContainer>
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <DashboardContainer>
      <DashboardHeader />

      {/* Sticky Navigation for System Admin */}
      <ProtectedContent requiredRoles={['SystemAdmin']}>
        {dashboardData?.systemAdminData && (
          <DashboardNavigation sections={getAdminDashboardSections()} />
        )}
      </ProtectedContent>

      {/* Sales Rep Dashboard - New Tabbed Version */}
      <ProtectedContent requiredRoles={['SalesRep', 'Secretary']}>
        {dashboardData && (
          <SalesRepDashboard
            dashboardData={dashboardData}
            onDataUpdate={handleReminderUpdate}
          />
        )}
      </ProtectedContent>

      <DashboardGrid>
        {/* Common reminders and calendar widgets for non-sales rep users */}
        <ProtectedContent requiredRoles={['CaseManager', 'SystemAdmin', 'Secretary']}>
          <Section id="reminders">
            <RemindersWidget
              reminders={{
                today: dashboardData.remindersSummary.todayReminders,
                overdue: dashboardData.remindersSummary.overdueReminders,
                upcoming: dashboardData.remindersSummary.upcomingReminders
              }}
              counts={{
                todayCount: dashboardData.remindersSummary.todayCount,
                overdueCount: dashboardData.remindersSummary.overdueCount,
                upcomingCount: dashboardData.remindersSummary.upcomingCount
              }}
              onReminderUpdate={handleReminderUpdate}
            />

            <CalendarWidget />
          </Section>
        </ProtectedContent>

        {/* Case Manager Dashboard */}
        <ProtectedContent requiredRoles={['CaseManager', 'Secretary']}>
          {dashboardData && (
            <CaseManagerDashboard
              dashboardData={dashboardData}
              onDataUpdate={handleReminderUpdate}
            />
          )}
        </ProtectedContent>

        {/* System Admin Dashboard - NEW ENHANCED VERSION */}
        <ProtectedContent requiredRoles={['SystemAdmin']}>
          {dashboardData.systemAdminData && (
            <>
              {/* Alerts & Urgent Tasks - MOVED TO TOP */}
              <Section id="alerts">
                <AdminAlertsWidget data={dashboardData.systemAdminData.urgentTasksSummary} />
              </Section>

              {/* Status Distribution Cards - NEW */}
              <Section id="status-distribution">
                <StatusDistributionGrid>
                  {dashboardData.systemAdminData.clientStatusDistribution && (
                    <StatusDistributionCard
                      title="התפלגות סטטוסי לקוחות"
                      statusDistribution={dashboardData.systemAdminData.clientStatusDistribution}
                      icon={<Users size={20} />}
                    />
                  )}
                  {dashboardData.systemAdminData.leadStatusDistribution && (
                    <StatusDistributionCard
                      title="התפלגות סטטוסי לידים"
                      statusDistribution={dashboardData.systemAdminData.leadStatusDistribution}
                      icon={<ClipboardList size={20} />}
                    />
                  )}
                </StatusDistributionGrid>
              </Section>

              {/* Financial Overview + Cash Flow */}
              <Section id="financial">
                <AdminFinancialOverview data={dashboardData.systemAdminData.financialOverview} />
                <CashFlowTrendChart data={dashboardData.systemAdminData.cashFlowTrend} />
              </Section>

              {/* Sales Metrics + Cases Metrics (under status-distribution / ביצועים) */}
              <AdminSalesMetrics
                performance={dashboardData.systemAdminData.systemPerformance}
                pipeline={dashboardData.systemAdminData.systemPipeline}
                newLeadsCount={dashboardData.systemAdminData.systemNewLeadsCount}
                signedThisMonthCount={dashboardData.systemAdminData.systemSignedThisMonthCount}
                topSalesReps={dashboardData.systemAdminData.topSalesReps}
              />

              <AdminCasesMetrics
                metrics={dashboardData.systemAdminData.casesMetrics}
                topCaseManagers={dashboardData.systemAdminData.topCaseManagers}
              />

              {/* Special Payments Widget */}
              <Section id="special-payments">
                {dashboardData.systemAdminData.specialPaymentsSummary && (
                  <AdminSpecialPaymentsWidget
                    data={dashboardData.systemAdminData.specialPaymentsSummary}
                  />
                )}
              </Section>

              {/* עמלות נציגי מכירות */}
              <Section id="commissions">
                {dashboardData.systemAdminData.commissionsOverview && (
                  <AdminCommissionsOverview
                    data={dashboardData.systemAdminData.commissionsOverview}
                  />
                )}
              </Section>
            </>
          )}
        </ProtectedContent>
      </DashboardGrid>

      {/* Back to Top Button */}
      <BackToTopButton />
    </DashboardContainer>
  );
};

export default DashboardPage;